import React from 'react';
import BrandingFooter from '../common/BrandingFooter';
import MatchWidgetShell from '../common/MatchWidgetShell';
import TeamBadge, { pickTeamLogo } from '../common/TeamBadge';
import { useWidgetLabels } from '../common/WidgetLabelsContext';
import { countryFlagEmoji } from '../../utils/countryFlags';
import { playerInitials, positionMeta } from './playerUtils';

function StatPill({ label, value }) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <span className="sw-squad-stat">
      <span className="sw-squad-stat__val">{value}</span>
      <span className="sw-squad-stat__lbl">{label}</span>
    </span>
  );
}

function PlayerCard({ row, t }) {
  const pos = positionMeta(row.position);
  const flag = countryFlagEmoji(row.nationality);

  return (
    <article className="sw-squad-card">
      <div className="sw-squad-card__top">
        {row.number != null ? (
          <span className="sw-squad-card__number">{row.number}</span>
        ) : (
          <span className="sw-squad-card__number sw-squad-card__number--na">—</span>
        )}
        <span className="sw-squad-card__avatar" aria-hidden>
          {playerInitials(row.player)}
        </span>
        <div className="sw-squad-card__head min-w-0 flex-1">
          <h3 className="sw-squad-card__name">{row.player}</h3>
          <div className="sw-squad-card__tags">
            <span className={`sw-injury-pos sw-injury-pos--${pos.tone}`}>{pos.label}</span>
            {row.isCaptain && (
              <span className="sw-squad-badge sw-squad-badge--captain">{t('captain')}</span>
            )}
            {row.injured && (
              <span className="sw-squad-badge sw-squad-badge--injured">{t('injured')}</span>
            )}
          </div>
        </div>
      </div>

      <div className="sw-squad-card__details">
        {row.nationality ? (
          <div className="sw-squad-detail">
            <span className="sw-squad-detail__label">{t('nationality')}</span>
            <span className="sw-squad-detail__value">
              {flag && <span className="sw-squad-flag" aria-hidden>{flag}</span>}
              {row.nationality}
            </span>
          </div>
        ) : null}
        {row.age ? (
          <div className="sw-squad-detail">
            <span className="sw-squad-detail__label">{t('age')}</span>
            <span className="sw-squad-detail__value">{row.age}</span>
          </div>
        ) : null}
      </div>

      <div className="sw-squad-card__stats">
        <StatPill label={t('appearances')} value={row.appearances} />
        <StatPill label={t('goals')} value={row.goals} />
        <StatPill label={t('assists')} value={row.assists} />
        <StatPill label={t('minutes')} value={row.minutes} />
        <StatPill label={t('rating')} value={row.rating} />
        {(row.yellowCards > 0 || row.redCards > 0) && (
          <StatPill
            label={t('cards')}
            value={`${row.yellowCards ?? 0}Y ${row.redCards ?? 0}R`}
          />
        )}
      </div>
    </article>
  );
}

export default function PlayerList({
  showBranding = false,
  competitionLabel = '',
  players = [],
  selectedTeam = null,
  teamLogo = null,
  teamLogoProxy = null,
}) {
  const t = useWidgetLabels();
  const logoRow = {
    team: selectedTeam,
    teamLogo: teamLogo || players[0]?.teamLogo,
    teamLogoProxy: teamLogoProxy || players[0]?.teamLogoProxy,
  };
  const teamLogoUrl = pickTeamLogo(logoRow);

  const subtitle = selectedTeam
    ? `${players.length} ${t('squadPlayers')}`
    : t('selectTeamSquad');

  return (
    <div style={{ color: 'var(--sw-text)' }}>
      <MatchWidgetShell
        competitionLabel={competitionLabel}
        title={t('playerList')}
        subtitle={subtitle}
        badge={
          selectedTeam && players.length > 0 ? (
            <span className="sw-squad-header-badge">{players.length}</span>
          ) : null
        }
      >
        {!selectedTeam ? (
          <div className="sw-squad-empty">
            <p className="sw-squad-empty__title">{t('selectTeamSquad')}</p>
            <p className="sw-squad-empty__text">{t('selectTeamSquadHint')}</p>
          </div>
        ) : (
          <>
            <div className="sw-squad-hero">
              <div className="sw-squad-hero__glow" aria-hidden />
              <TeamBadge name={selectedTeam} logoUrl={teamLogoUrl} size={56} />
              <h2 className="sw-squad-hero__title">{selectedTeam}</h2>
              <p className="sw-squad-hero__sub">{t('fullSquad')}</p>
            </div>
            <div className="sw-squad-body">
              {players.length ? (
                <div className="sw-squad-grid">
                  {players.map((p) => (
                    <PlayerCard key={`${p.id || p.player}-${p.number}`} row={p} t={t} />
                  ))}
                </div>
              ) : (
                <p className="sw-squad-empty__text py-10 text-center">
                  {t('noSquadData')}
                </p>
              )}
            </div>
          </>
        )}
      </MatchWidgetShell>
      {showBranding && <BrandingFooter />}
    </div>
  );
}
